import { useContext } from 'react';
import useSWR from 'swr';
import { useAsyncError } from '../../common/error/useAsyncError';
import { getFromApi } from '../../common/utils/apiHelper';
import { callbackToPromise } from '../../common/utils/promiseHelper';
import { ConfigContext } from '../configuration/ConfigContext';
import { GetPropertyAssociationConfigResponse } from './models/GetPropertyAssociationConfigResponse';
import { PropertyAssociationInfo } from './models/PropertyAssociationInfo';
import { PropertyAssociationList } from './models/PropertyAssociationList';
import { PropertyAssociationSearchResult } from './models/PropertyAssociationSearchResult';

export const getPropertyAssociation = (apiBaseUrl: string, id: string, onSuccess: (data: PropertyAssociationInfo) => void, onFail: () => void): void => {
    getFromApi(`${apiBaseUrl}/propertyassociations/${id}`, onSuccess, onFail);
};

export const getPropertyAssociations = (apiBaseUrl: string, onSuccess: (data: PropertyAssociationList) => void, onFail: () => void): void => {
    getFromApi(`${apiBaseUrl}/propertyassociations`, onSuccess, onFail);
};

export const getPropertyAssociationConfig = (apiBaseUrl: string, id: string, onSuccess: (data: GetPropertyAssociationConfigResponse) => void, onFail: () => void): void => {
    getFromApi(`${apiBaseUrl}/propertyassociations/${id}/config`, onSuccess, onFail);
};

export const searchPropertyAssociation = (apiBaseUrl: string, searchTerm: string, offset: number, onSuccess: (data: PropertyAssociationSearchResult) => void, onFail: () => void): void => {
    const pageSize = 10;
    const page = Math.floor(offset / pageSize);
    getFromApi(`${apiBaseUrl}/propertyassociations/page?search=${searchTerm}&page=${page}&pageSize=${pageSize}`, onSuccess, onFail);
};

// New Async Functions
export const getPropertyAssociationsAsync = (apiBaseUrl: string): Promise<PropertyAssociationList> => {
    return callbackToPromise<PropertyAssociationList>((onSuccess, onFail) => {
        getPropertyAssociations(apiBaseUrl, onSuccess, onFail);
    });
};

export const getPropertyAssociationAsync = (apiBaseUrl: string, id: string): Promise<PropertyAssociationInfo> => {
    return callbackToPromise<PropertyAssociationInfo>((onSuccess, onFail) => {
        getPropertyAssociation(apiBaseUrl, id, onSuccess, onFail);
    });
};

export const getPropertyAssociationConfigAsync = (apiBaseUrl: string, id: string): Promise<GetPropertyAssociationConfigResponse> => {
    return callbackToPromise<GetPropertyAssociationConfigResponse>((onSuccess, onFail) => {
        getPropertyAssociationConfig(apiBaseUrl, id, onSuccess, onFail);
    });
};

export const usePropertyAssociations = (fallbackData: PropertyAssociationList = { isAdmin: false, propertyAssociations: [] }): PropertyAssociationList => {
    const { apiBaseUrl } = useContext(ConfigContext);

    const { data, error } = useSWR<PropertyAssociationList>(`${apiBaseUrl}/propertyassociations`, {
        fallbackData,
        revalidateOnMount: false, // Don't refetch on mount since loader just fetched
    });
    const throwAsyncError = useAsyncError();

    if (error) {
        throwAsyncError(new Error('Kunde inte hämta engagemang.'));
    }

    // With fallbackData, data is guaranteed to exist
    return data ?? fallbackData;
};

export const usePropertyAssociation = (propertyAssociationId: string, fallbackData?: PropertyAssociationInfo) => {
    const { apiBaseUrl } = useContext(ConfigContext);
    const shouldFetch = Boolean(propertyAssociationId);

    const { data, error, isLoading } = useSWR<PropertyAssociationInfo>(shouldFetch ? `${apiBaseUrl}/propertyassociations/${propertyAssociationId}` : null, {
        fallbackData,
        revalidateOnMount: !fallbackData, // Don't revalidate if we have loader data
    });
    const throwAsyncError = useAsyncError();

    if (error) {
        throwAsyncError(new Error(`Kunde inte hämta engagemang med id ${propertyAssociationId}.`));
    }

    return {
        propertyAssociation: data,
        isLoading,
    };
};

export const usePropertyAssociationConfig = (propertyAssociationId: string, fallbackData?: GetPropertyAssociationConfigResponse) => {
    const { apiBaseUrl } = useContext(ConfigContext);
    const shouldFetch = Boolean(propertyAssociationId);

    const { data, error, isLoading } = useSWR<GetPropertyAssociationConfigResponse>(shouldFetch ? `${apiBaseUrl}/propertyassociations/${propertyAssociationId}/config` : null, {
        fallbackData,
        revalidateOnMount: !fallbackData, // Don't revalidate if we have loader data
    });
    const throwAsyncError = useAsyncError();

    if (error) {
        throwAsyncError(new Error(`Kunde inte hämta konfiguration för engagemang med id ${propertyAssociationId}.`));
    }

    return {
        propertyAssociationConfig: data,
        isLoading,
    };
};
